import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  ListItemText,
  Button,
  Paper,
  Link as MuiLink,
} from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import OverLayLoader from '../../../components/OverLayLoader';
import { createErrorNotification } from '../../../utils/createErrorNotification';
import useTitle from '../../../hooks/useTitle';
import {
  getUploadsActionsList,
  uploadUploadsFile,
  getUploadsResult,
  getUploadsTemplate,
} from '../../../services/downloads';
import saveFile from '../../../utils/saveFile';
import { useAuth } from '../../../context/auth/AuthContext';

const DownloadsView = () => {
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedTaskAction, setSelectedTaskAction] = useState('');
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);

  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [uploadsResult, setUploadsResult] = useState('');

  const fileInputRef = useRef(null);

  const { user } = useAuth();

  const { t, i18n } = useTranslation(['downloads', 'common', 'promptbar']);

  const title = t(`downloads:downloads`);
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  const selectedTask = useMemo(
    () => taskOptions.find(opt => opt.action === selectedTaskAction) || {},
    [selectedTaskAction, taskOptions],
  );

  useEffect(() => {
    if (!user) {
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchUploadActions = async () => {
      try {
        setIsOptionsLoading(true);
        const params = { language_name: i18n.language };

        const result = await getUploadsActionsList(params, signal);

        if (result) {
          setTaskOptions(result.data);
          setSelectedTaskAction(result.data[0]?.action || '');
        }
      } catch (error) {
        console.log(error.message);
        createErrorNotification();
      } finally {
        setIsOptionsLoading(false);
      }
    };

    fetchUploadActions();

    return () => controller.abort();
  }, [i18n.language, user]);

  const handleFormSubmit = async () => {
    try {
      setIsLoading(true);
      const result = await getUploadsResult(uploadId);
      if (result) {
        setUploadsResult(result?.data?.report || '');
      }
      setIsLoading(false);
    } catch (error) {
      createErrorNotification();
      setIsLoading(false);
    }
  };

  const handleSelectChange = evt => {
    const value = evt.target.value;
    setUploadsResult('');
    setSelectedTaskAction(value);
  };

  const handleFileChange = async newFile => {
    try {
      handleReset();

      if (!newFile) {
        return;
      }

      setTimeout(() => setFile(newFile), 0);

      setIsLoading(true);

      const params = { action: selectedTaskAction };
      const data = { file: newFile };

      const response = await uploadUploadsFile(data, params);

      if (response?.data?.upload_id) {
        setUploadId(response.data.upload_id);
      } else {
        createErrorNotification();
      }

      setIsLoading(false);
    } catch (error) {
      setFile(null);
      setIsLoading(false);
      createErrorNotification();
    } finally {
      fileInputRef.current.value = null;
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsLoading(true);
      const result = await getUploadsTemplate(selectedTaskAction);
      if (result) {
        const blob = new Blob([result.data], {
          type: 'application/octet-stream',
        });
        saveFile(blob, `${selectedTaskAction}.xlsx`);
      }
    } catch (error) {
      createErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setUploadId(null);
    setUploadsResult('');
  };

  const isSaveBtnDisabled =
    !file || !selectedTaskAction || isLoading || !uploadId;

  return (
    <>
      {(isOptionsLoading || isLoading) && <OverLayLoader />}
      <Box px={15}>
        <Paper elevation={3} sx={{ padding: '2.5rem 2rem 2rem' }}>
          <Box
            component="form"
            autoComplete="off"
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
              <InputLabel id="InputLabel-1" shrink={false}>
                {t('downloads:task')}
              </InputLabel>
              <Select
                labelId="InputLabel-1"
                id="Select"
                size="small"
                name="task"
                value={selectedTaskAction}
                input={<OutlinedInput label={t('downloads:task')} />}
                onChange={handleSelectChange}
                IconComponent={KeyboardArrowDownIcon}
              >
                {taskOptions.map(opt => (
                  <MenuItem key={opt.action} value={opt.action}>
                    <ListItemText primary={opt.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
              <InputLabel id="InputLabel-2" shrink={false}>
                {t('downloads:requirements')}
              </InputLabel>
              <Paper className="textfield" elevation={0}>
                {selectedTask?.required_columns}
              </Paper>
            </FormControl>

            <FormControl fullWidth variant="standard" sx={{ mt: '2.5rem' }}>
              <InputLabel id="InputLabel-3" shrink={false}>
                {t('downloads:excel_file')}
              </InputLabel>
              <MuiFileInput
                inputRef={fileInputRef}
                value={file}
                onChange={handleFileChange}
                InputProps={{
                  inputProps: {
                    accept: '.xlsx',
                  },
                  startAdornment: <AttachFileIcon />,
                }}
              />
            </FormControl>

            <FormControl fullWidth variant="standard" sx={{ mt: '1rem' }}>
              <MuiLink
                component="button"
                type="button"
                underline="always"
                onClick={handleDownloadTemplate}
                disabled={!selectedTaskAction || isLoading}
                sx={{ font: 'inherit', mb: '0.5rem' }}
              >
                {t('downloads:download_template')}
              </MuiLink>
            </FormControl>

            <Box sx={{ display: 'flex', mt: '2rem', justifyContent: 'center' }}>
              <Button
                type="button"
                color="secondary"
                sx={{ mr: 1 }}
                variant="contained"
                onClick={handleReset}
              >
                {t('promptbar:cancel')}
              </Button>
              <Button
                type="button"
                variant="contained"
                disabled={isSaveBtnDisabled}
                onClick={handleFormSubmit}
              >
                {t('promptbar:execute')}
              </Button>
            </Box>

            {/* результат */}
            {uploadsResult && (
              <FormControl fullWidth variant="standard" sx={{ mt: '1.5rem' }}>
                <InputLabel id="InputLabel-4" shrink={false}>
                  {t('downloads:validation')}
                </InputLabel>
                <Paper className="textfield" elevation={0}>
                  {uploadsResult}
                </Paper>
              </FormControl>
            )}
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default DownloadsView;
